import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

type PresenceState = Record<string, Array<any>>;

type ActiveDraw = {
  key: string;
  sessionId: string;
  min: number;
  max: number;
  count: number;
  started_at: string;
  path: string;
  referrer: string;
  platform: string;
  street_address?: string;
  city?: string;
  country?: string;
  region?: string;
  numbersUsed?: number;
  totalNumbers?: number;
};

export const ActiveDraws = () => {
  const { toast } = useToast();
  const [active, setActive] = useState<ActiveDraw[]>([]);
  const [rigValues, setRigValues] = useState<Record<string, string>>({});
  const [forceLastNumbers, setForceLastNumbers] = useState<Record<string, string>>({});
  const [endingDigits, setEndingDigits] = useState<Record<string, string>>({});
  const [drawStats, setDrawStats] = useState<Record<string, { used: number; total: number }>>({});

  useEffect(() => {
    const channel = supabase.channel('draws');

    const recompute = async () => {
      const state = channel.presenceState() as PresenceState;
      const list: ActiveDraw[] = [];
      Object.entries(state).forEach(([key, arr]) => {
        arr.forEach((payload: any) => {
          if (payload?.sessionId) {
            list.push({ key, ...payload });
          }
        });
      });
      // sort by started_at desc
      list.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
      
      // Fetch stats for each session
      const newStats: Record<string, { used: number; total: number }> = {};
      for (const draw of list) {
        const total = draw.max - draw.min + 1;
        
        // Query number_generations for this session to count used numbers
        const { data } = await supabase
          .from('number_generations')
          .select('generated_numbers')
          .gte('created_at', draw.started_at)
          .eq('min_value', draw.min)
          .eq('max_value', draw.max);
        
        const usedCount = data?.reduce((sum, row) => sum + (row.generated_numbers?.length || 0), 0) || 0;
        newStats[draw.sessionId] = { used: usedCount, total };
      }
      
      setDrawStats(newStats);
      setActive(list);
    };

    channel
      .on('presence', { event: 'sync' }, recompute)
      .on('presence', { event: 'join' }, recompute)
      .on('presence', { event: 'leave' }, recompute)
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const rigForSession = async (draw: ActiveDraw) => {
    const val = rigValues[draw.sessionId];
    const number = parseInt(val, 10);
    if (Number.isNaN(number)) {
      toast({ title: 'Invalid number', description: 'Enter a valid integer', variant: 'destructive' });
      return;
    }
    if (number < draw.min || number > draw.max) {
      toast({ title: 'Out of range', description: `Must be between ${draw.min}-${draw.max}`, variant: 'destructive' });
      return;
    }

    const { error } = await supabase.from('queued_numbers').insert({
      number,
      min_range: draw.min,
      max_range: draw.max,
      used: false,
      session_id: draw.sessionId,
    });

    if (error) {
      console.error('Queue insert error', error);
      toast({ title: 'Error', description: 'Failed to queue rigged number', variant: 'destructive' });
      return;
    }

    toast({ title: 'Rigged!', description: `Number ${number} queued for this draw.` });
    setRigValues((s) => ({ ...s, [draw.sessionId]: '' }));
  };

  const saveSessionSettings = async (draw: ActiveDraw) => {
    const forceLastVal = forceLastNumbers[draw.sessionId]?.trim();
    const endingDigitsVal = endingDigits[draw.sessionId]?.trim();
    
    const forceLastNum = forceLastVal ? parseInt(forceLastVal, 10) : null;
    if (forceLastVal && (Number.isNaN(forceLastNum) || forceLastNum! < draw.min || forceLastNum! > draw.max)) {
      toast({ title: 'Invalid force last number', description: `Must be between ${draw.min}-${draw.max}`, variant: 'destructive' });
      return;
    }

    const { error } = await supabase.from('session_settings').upsert({
      session_id: draw.sessionId,
      force_last_number: forceLastNum,
      ending_digits: endingDigitsVal || null,
    }, { onConflict: 'session_id' });

    if (error) {
      console.error('Session settings error', error);
      toast({ title: 'Error', description: 'Failed to save session settings', variant: 'destructive' });
      return;
    }

    toast({ title: 'Saved!', description: 'Session settings updated.' });
  };

  if (active.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-2xl font-bold mb-2">Live Draws</h2>
        <p className="text-sm text-muted-foreground mb-4">See active spins in real time and rig them instantly.</p>
        <div className="text-center py-8 text-muted-foreground">
          <p>No active draws right now. Start a spin to see it appear here!</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-2xl font-bold mb-2">Live Draws</h2>
      <p className="text-sm text-muted-foreground mb-4">See active spins in real time and rig them instantly.</p>
      <div className="space-y-3">
        {active.map((d) => (
          <div key={`${d.key}-${d.sessionId}`} className="flex flex-col gap-3 p-4 bg-muted rounded-lg">
            <div className="text-sm">
              <div className="font-medium">Session: {d.sessionId.slice(0, 8)}…</div>
              <div>Range: {d.min}-{d.max} • Count: {d.count}</div>
              {drawStats[d.sessionId] && (
                <div className="font-semibold text-primary">
                  Numbers Used: {drawStats[d.sessionId].used} / {drawStats[d.sessionId].total} • 
                  Remaining: {drawStats[d.sessionId].total - drawStats[d.sessionId].used}
                </div>
              )}
              <div>From: {d.street_address ? d.street_address : `${d.city ? `${d.city}, ` : ''}${d.country || 'Unknown'}${d.region ? ` (${d.region})` : ''}`}</div>
              <div>Path: {d.path} • Referrer: {d.referrer || 'direct'}</div>
              <div className="text-muted-foreground">Started: {new Date(d.started_at).toLocaleString()}</div>
            </div>
            
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={`rig-${d.sessionId}`} className="sr-only">Number</Label>
                <Input
                  id={`rig-${d.sessionId}`}
                  type="number"
                  placeholder="Force number"
                  value={rigValues[d.sessionId] ?? ''}
                  onChange={(e) => setRigValues((s) => ({ ...s, [d.sessionId]: e.target.value }))}
                  className="flex-1"
                />
                <Button onClick={() => rigForSession(d)}>Rig now</Button>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1">
                  <Label htmlFor={`force-${d.sessionId}`} className="text-xs text-muted-foreground">Force Last Number</Label>
                  <Input
                    id={`force-${d.sessionId}`}
                    type="number"
                    placeholder="e.g., 7"
                    value={forceLastNumbers[d.sessionId] ?? ''}
                    onChange={(e) => setForceLastNumbers((s) => ({ ...s, [d.sessionId]: e.target.value }))}
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor={`digits-${d.sessionId}`} className="text-xs text-muted-foreground">Ending Digits</Label>
                  <Input
                    id={`digits-${d.sessionId}`}
                    placeholder="e.g., 3,7"
                    value={endingDigits[d.sessionId] ?? ''}
                    onChange={(e) => setEndingDigits((s) => ({ ...s, [d.sessionId]: e.target.value }))}
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={() => saveSessionSettings(d)} variant="secondary" className="w-full sm:w-auto">Save</Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
